// The Local room's projection layer: the same visual grammar as the Session
// Primitive — tab rows, an addressed lineage, one seat per level — folded
// from UNGOVERNED session shells instead of a World's Session index. Local
// deliberately never synthesizes a `WorldRoomIndex`: that contract is a
// governed projection (S_harmony_session root, Goals, ledger cut), and ungoverned
// work must never wear it. The shared thing is the grammar, not the type.
//
// Tree law is the entitlement fold's, already paid for there: archived rows
// are gone, a live child of a missing parent surfaces as a root, cycles
// break instead of recursing, parent pointers bind within one environment.

import {
  projectEntitlementTree,
  type EntitlementSessionNode,
  type EntitlementThreadInput,
} from "./entitlementTree";

/** The minimal shell facts the Local room folds; structural on purpose. */
export interface LocalSessionShellInput extends EntitlementThreadInput {}

export interface LocalSessionNode {
  /** Stable per-session identity: `environmentId:threadId`. */
  readonly id: string;
  readonly parentId: string | null;
  readonly label: string;
  readonly environmentId: string;
  readonly threadId: string;
}

export interface LocalSessionRow {
  /** null names the root row; otherwise the lineage node owning the row. */
  readonly parentId: string | null;
  readonly sessions: ReadonlyArray<LocalSessionNode>;
}

export function localSessionNodeId(environmentId: string, threadId: string): string {
  return `${environmentId}:${threadId}`;
}

export function localSessionLabel(title: string | null): string {
  return title?.trim() || "Untitled session";
}

interface LocalForest {
  readonly roots: ReadonlyArray<EntitlementSessionNode>;
  readonly nodeById: ReadonlyMap<string, EntitlementSessionNode>;
  readonly parentById: ReadonlyMap<string, string | null>;
  readonly titleById: ReadonlyMap<string, string | null>;
}

function foldLocalForest(shells: ReadonlyArray<LocalSessionShellInput>): LocalForest {
  const local = shells.filter((shell) => shell.worldKey === null);
  const titleById = new Map(
    local.map((shell) => [localSessionNodeId(shell.environmentId, shell.threadId), shell.title]),
  );
  const roots =
    projectEntitlementTree({ worldKeys: [], threads: local }).find(
      (group) => group.worldKey === null,
    )?.sessions ?? [];
  const nodeById = new Map<string, EntitlementSessionNode>();
  const parentById = new Map<string, string | null>();
  const visit = (node: EntitlementSessionNode, parentId: string | null): void => {
    const id = localSessionNodeId(node.environmentId, node.threadId);
    nodeById.set(id, node);
    parentById.set(id, parentId);
    for (const child of node.children) visit(child, id);
  };
  for (const root of roots) visit(root, null);
  return { roots, nodeById, parentById, titleById };
}

function toView(node: EntitlementSessionNode, parentId: string | null): LocalSessionNode {
  return {
    id: localSessionNodeId(node.environmentId, node.threadId),
    parentId,
    label: localSessionLabel(node.title),
    environmentId: node.environmentId,
    threadId: node.threadId,
  };
}

/** All local session nodes; folded guests need not be on the current canvas lineage. */
export function projectLocalSessionNodes(
  shells: ReadonlyArray<LocalSessionShellInput>,
): ReadonlyArray<LocalSessionNode> {
  const forest = foldLocalForest(shells);
  return [...forest.nodeById].map(([id, node]) => toView(node, forest.parentById.get(id) ?? null));
}

/**
 * The addressed lineage, root-first — the levels the Local room physically
 * renders, one rail each. An id the forest does not hold (a governed thread,
 * a stale address) yields the empty lineage; callers fall back to the root
 * row exactly as the world view falls back to `S_harmony_session`.
 */
export function projectLocalSessionLineage(
  shells: ReadonlyArray<LocalSessionShellInput>,
  activeId: string,
): ReadonlyArray<LocalSessionNode> {
  const forest = foldLocalForest(shells);
  if (!forest.nodeById.has(activeId)) return [];
  const lineage: LocalSessionNode[] = [];
  let cursor: string | null = activeId;
  while (cursor !== null) {
    const node = forest.nodeById.get(cursor);
    if (node === undefined) break;
    const parentId: string | null = forest.parentById.get(cursor) ?? null;
    lineage.unshift(toView(node, parentId));
    cursor = parentId;
  }
  return lineage;
}

/**
 * The room's tab rows, the same grammar as `projectWorldSessionRows`: the root
 * row always exists (Local's primary sessions), and each lineage level with
 * children owns one nested row. A childless level owns no row.
 */
export function projectLocalSessionRows(
  shells: ReadonlyArray<LocalSessionShellInput>,
  activeId: string,
): ReadonlyArray<LocalSessionRow> {
  const forest = foldLocalForest(shells);
  const rows: LocalSessionRow[] = [
    { parentId: null, sessions: forest.roots.map((root) => toView(root, null)) },
  ];
  for (const level of projectLocalSessionLineage(shells, activeId)) {
    const node = forest.nodeById.get(level.id);
    if (node === undefined || node.children.length === 0) continue;
    rows.push({
      parentId: level.id,
      sessions: node.children.map((child) => toView(child, level.id)),
    });
  }
  return rows;
}
